import * as React from 'react';

import '@ant-design/compatible/assets/index.css';

import {
	Layout,
	Row,
	Col,
	Card,
	Button,
	Tooltip,
	Alert,
	Modal,
	Input,
	Form,
	message,
	Popover,
} from 'antd';
import { connect } from 'react-redux';
import { useParams, withRouter } from 'react-router';
import { useCallback, useMemo, useState } from 'react';
import { AddressDisplay } from '../address_display/AddressDisplay';
import { checkAndFormatPhoneNumber, optionalFilter } from '../../utils/DataFormatterUtils';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { KeyValueDisplay } from '../key_value_display/KeyValueDisplay';
import Mapbox from '../mapbox/Mapbox';
import {
	businessTypeMap,
	encodeNumberToBase62,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { updateNavbarState } from '../../actions/navbar_actions';
import EditContractorClientContactDetails from '../EditContractorClientContactDetails';
import ContractorClientContactDirectory from '../contractor_client_contact_directory/ContractorClientContactDirectory';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	contractorClientsRestCrudThunksForBuyer,
	contractorClientsRestCrudThunksForSupplier,
	inviteAdminsForClientBuyerCompany,
} from '../../thunks/contractor_clients_thunks';
import { TwoLineList } from '../two_line_list/TwoLineList';
import OWEditableNotesComponent from '../common/OWEditableNotesComponent';
import FileUploader from '../file_uploader/FileUploader';
import ClientPortalWidget from '../client_portal_widget';
import posthog from 'posthog-js';
import { portalsCrudThunksForSupplier } from '../../thunks/portal_thunks';
import { withCurrentUser } from '../../hooks/withCurrentUser';
import { withUserType } from '../withUserType';
import { buyersRestCrudThunksForSupplier } from '../../thunks/buyers_thunks';
import EmailComponentAntdV4 from '../email_component/EmailComponentAntdV4';
import { contractorLocationsRestCrudThunksForSupplier } from '../../thunks/contractor_locations_thunks';
import { DashboardOutlined, LinkOutlined } from '@ant-design/icons';

const { Content } = Layout;

const ContractorClientDetailDetailsPage = ({
	contractorClient,
	contractorClientLoading,
	updateNavbar,
	userType,
	fetchContractorClient,
	updateContractorClient,
	fetchPortals,
	currentUser,
	createClientPortal,
	createBuyerCompany,
	inviteAdminsForClientBuyerCompany,
	fetchContractorLocations,
}) => {
	const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
	const { id: contractorClientId } = useParams();
	const [dataLoading, setDataLoading] = useState(false);
	const [portal, setPortal] = React.useState(null);
	const [creatingPortal, setCreatingPortal] = React.useState(false);
	const [portalFetched, setPortalFetched] = React.useState(false);
	const [showPrimaryContactEmailModal, setShowPrimaryContactEmailModal] = useState(false);
	const [submittingBuyerCompany, setSubmittingBuyerCompany] = useState(false);
	const [showClientPortalInviteModal, setShowClientPortalInviteModal] = useState(false);
	const [clientPortalInviteLoading, setClientPortalInviteLoading] = useState(false);
	const formRef = React.useRef<any>();
	const clientPortalInviteFormRef = React.useRef<any>();
	const [clientHasLocations, setClientHasLocations] = useState(false);

	const showRequestPortalWidget = posthog.isFeatureEnabled('client-portal-self-serve');

	const fetchPortal = useCallback(() => {
		setDataLoading(true);
		setPortalFetched(true);
		fetchPortals(
			{
				contractorClientId: contractorClient.id,
			},
			'clientPortals'
		)
			.then((data) => {
				if (data && data.length > 0) {
					setPortal(nullSafeGet('0', data));
				} else {
					setPortal(null);
				}
			})
			.finally(() => {
				setDataLoading(false);
			});
	}, [contractorClient, fetchPortals]);

	React.useEffect(() => {
		if (
			contractorClient &&
			contractorClient.id &&
			!nullSafeGet('clientBuyerCompany', contractorClient) &&
			!portal &&
			!portalFetched
		) {
			fetchPortal();
		}
	}, [fetchPortal, portalFetched]);

	React.useEffect(() => {
		if (
			contractorClient &&
			contractorClient.id &&
			!nullSafeGet('clientBuyerCompany', contractorClient)
		) {
			fetchContractorLocations({ contractorClientId: contractorClientId }).then((locations) => {
				setClientHasLocations(locations.length > 0);
			});
		}
	}, []);

	const fetchData = useCallback(() => {
		setDataLoading(true);
		fetchContractorClient(contractorClientId).then(() => setDataLoading(false));
	}, [setDataLoading, fetchContractorClient, contractorClientId]);

	const handleOpenPrimaryContactEmailModal = useCallback(() => {
		setShowPrimaryContactEmailModal(true);
	}, []);

	const handleOpenClientPortalInviteModal = useCallback(() => {
		setShowClientPortalInviteModal(true);
	}, []);

	const handleSubmitBuyerCompany = useCallback(
		(values) => {
			setSubmittingBuyerCompany(true);
			createBuyerCompany({
				addressInput: contractorClient.address,
				longitude: nullSafeGet('longitude', contractorClient),
				latitude: nullSafeGet('latitude', contractorClient),
				'primaryAddress-streetAddress1': nullSafeGet('address.streetAddress1', contractorClient),
				'primaryAddress-streetAddress2Opt': nullSafeGet(
					'address.streetAddress2Opt',
					contractorClient
				),
				'primaryAddress-city': nullSafeGet('address.city', contractorClient),
				'primaryAddress-region': nullSafeGet('address.region', contractorClient),
				'primaryAddress-country': nullSafeGet('address.country', contractorClient),
				status: 'active',
				name: nullSafeGet('name', contractorClient),
				currencyId: nullSafeGet('currencyId', contractorClient),
				displayName: nullSafeGet('name', contractorClient),
				primaryContactEmail: values.primaryContactEmail.trim(),
				primaryAddress: {
					streetAddress1: nullSafeGet('address.streetAddress1', contractorClient),
					streetAddress2Opt: nullSafeGet('address.streetAddress2Opt', contractorClient),
					city: nullSafeGet('address.city', contractorClient),
					region: nullSafeGet('address.region', contractorClient),
					country: nullSafeGet('address.country', contractorClient),
				},
				contractorClientId: nullSafeGet('id', contractorClient),
				managedBySupplierCompanyId: nullSafeGet('contractorSupplierFacilityId', contractorClient),
			})
				.then(() => {
					message.success('Client portal created successfully');
					fetchData();
					setShowPrimaryContactEmailModal(false);
				})
				.catch((error) => {
					message.error('Failed to create client portal. Please try again.');
				})
				.finally(() => {
					setSubmittingBuyerCompany(false);
				});
		},
		[contractorClient, createBuyerCompany]
	);

	const handleClientPortalInvite = async (values) => {
		setClientPortalInviteLoading(true);
		inviteAdminsForClientBuyerCompany(
			nullSafeGet('clientBuyerCompany.id', contractorClient),
			values.email,
			{
				email: values.email,
				nameGiven: values.nameGiven,
				nameFamily: values.nameFamily,
				roles: ['BUYER_ADMIN'],
				hasAccessToAllLocations: true,
				byLocationOrBrand: 'location',
				brandIds: [],
				locationIds: [],
				isSharedContact: false,
			}
		)
			.then(() => {
				message.success('Client portal invite sent successfully');
				setShowClientPortalInviteModal(false);
				clientPortalInviteFormRef.current.resetFields();
			})
			.catch((error) => {
				message.error('Failed to send client portal invite');
			})
			.finally(() => {
				setClientPortalInviteLoading(false);
			});
	};

	const contactInfo = useMemo(() => {
		if (contractorClientLoading || dataLoading) return {};
		return {
			Address: nullSafeGet('address', contractorClient) ? (
				<AddressDisplay address={nullSafeGet('address', contractorClient)} />
			) : (
				'--'
			),
			Phone: optionalFilter(checkAndFormatPhoneNumber(contractorClient.phoneNumber)),
			Email: optionalFilter(contractorClient.primaryContactEmail),
			Website: optionalFilter(contractorClient.website),
		};
	}, [contractorClientLoading, dataLoading, contractorClient]);

	const contactInfoKeyAliases = {
		Address: 'Address',
		Email: 'Email',
		Phone: 'Phone Number',
		Website: 'Website',
	};

	const detailsDisplay = useMemo(
		() => [
			...(contractorClient.currencyId
				? [
						{
							key: 'Currency',
							value: contractorClient.currencyId,
						},
				  ]
				: []),
			...(contractorClient.businessType
				? [
						{
							key: 'Business Type',
							value: businessTypeMap[contractorClient.businessType],
						},
				  ]
				: []),
			...(contractorClient.defaultWorkOrderSubscribers
				? [
						{
							key: 'Default Work Order Subscribers',
							value: contractorClient.defaultWorkOrderSubscribers.join(', '),
						},
				  ]
				: []),
		],
		[contractorClient]
	);

	const handleSaveInternalNotes = useCallback(
		(propName) => (notes) => {
			const updatedLocation = {
				...contractorClient,
				[propName]: notes,
			};
			return updateContractorClient(updatedLocation);
		},
		[contractorClient, updateContractorClient]
	);
	const handleInternalFileAttachmentUploadChange = useCallback(
		(internalAttachments) => {
			const updatedContractorClient = {
				...contractorClient,
				internalAttachments: internalAttachments,
			};
			updateContractorClient(updatedContractorClient);
		},
		[contractorClient, updateContractorClient]
	);

	return (
		<Content className="sourcingDetailPage" style={{ position: 'relative', padding: 0 }}>
			{contractorClientLoading ? (
				<PageLoadingPlaceholder />
			) : (
				<div style={{ paddingBottom: 40, paddingTop: 8, flex: 1 }}>
					<EditContractorClientContactDetails
						visible={showEditDetailsModal}
						close={() => setShowEditDetailsModal(false)}
						formData={contractorClient}
					/>
					{!portal &&
					showRequestPortalWidget &&
					!nullSafeGet('clientBuyerCompany', contractorClient) ? (
						<Alert
							message="Create a Request Portal"
							description="A lightweight, dedicated link for a single client to send you new work orders — no login or account needed on their end."
							type="info"
							showIcon
							icon={<LinkOutlined />}
							style={{ marginBottom: 16 }}
							action={
								<Button
									loading={creatingPortal}
									onClick={() => {
										setCreatingPortal(true);
										createClientPortal({
											contractorClientId: contractorClient.id,
											customerSlug: `${currentUser.facility.id}-${encodeNumberToBase62(
												currentUser.facility.id
											)}`,
											clientSlug: `${contractorClient.id}-${encodeNumberToBase62(
												contractorClient.id
											)}`,
											customerLogo: nullSafeGet('facility.logoURL', currentUser),
										}).then(() => {
											setCreatingPortal(false);
											fetchPortal();
										});
									}}
									disabled={!clientHasLocations}
								>
									Create
								</Button>
							}
						/>
					) : null}

					{nullSafeGet('clientBuyerCompany', contractorClient) ||
					nullSafeGet('clientSupplierFacility', contractorClient) ? null : (
						<Alert
							message="Create a Client Portal"
							description="A branded portal where your client can submit new requests, track progress, and review past work orders — all in one place."
							type="success"
							icon={<DashboardOutlined />}
							showIcon
							style={{ marginBottom: 16 }}
							action={
								<Popover
									placement="left"
									content={
										clientHasLocations
											? ''
											: `You need to have at least one client location to create a client portal.`
									}
								>
									<Button
										disabled={!clientHasLocations}
										onClick={handleOpenPrimaryContactEmailModal}
									>
										Create
									</Button>
								</Popover>
							}
						/>
					)}
					<Row style={{}} gutter={16}>
						<Col span={16}>
							<div className="rowSpacing">
								<OWEditableNotesComponent
									id="contractorClientInternalNotes"
									tooltipMessage="Only visible to your team"
									title="Internal Notes"
									notes={nullSafeGet('internalNotes', contractorClient)}
									allowEdit={true}
									onSaveNotes={handleSaveInternalNotes('internalNotes')}
								/>
							</div>
							<Row className="rowSpacing" gutter={16}>
								<Col span={24}>
									<ContractorClientContactDirectory
										client={contractorClient}
										onSuccess={() => fetchData()}
									/>
								</Col>
							</Row>
						</Col>
						<Col span={8}>
							<Row gutter={16}>
								<Col span={24}>
									{showRequestPortalWidget &&
										!nullSafeGet('clientBuyerCompany', contractorClient) && (
											<ClientPortalWidget portal={portal} contractorClient={contractorClient} />
										)}
									{nullSafeGet('clientBuyerCompany', contractorClient) &&
									nullSafeGet('clientBuyerCompany.managedBySupplierCompanyId', contractorClient) ===
										nullSafeGet('facility.supplierCompanyId', currentUser) ? (
										<div className="rowSpacing">
											<Alert
												message="Client Portal created"
												description="You can now invite users to the client portal"
												type="info"
												showIcon
												action={<Button onClick={handleOpenClientPortalInviteModal}>Invite</Button>}
											/>
										</div>
									) : null}
									<div className="rowSpacing">
										<Tooltip placement="top" title="Only visible to your team">
											<Card title="Internal Attachments">
												<FileUploader
													roleType={userType}
													defaultFileList={nullSafeGetOrElse(
														'internalAttachments',
														contractorClient,
														[]
													)}
													handleUploadSuccess={handleInternalFileAttachmentUploadChange}
													uploaderType={'dragger'}
												/>
											</Card>
										</Tooltip>
									</div>
									{contractorClient.longitude && contractorClient.latitude ? (
										<Card title="Location">
											<div style={{ height: 400, position: 'relative' }}>
												<Mapbox
													longitude={contractorClient.longitude}
													latitude={contractorClient.latitude}
													baseLayerStyle="light"
													zoomLevel={7}
												/>
											</div>
										</Card>
									) : null}
									<div className="rowSpacing">
										<Card
											title={
												<Row justify={'space-between'}>
													<span>Contact Information</span>
													<Button
														type={'ghost'}
														key="edit"
														onClick={() => setShowEditDetailsModal(true)}
													>
														Edit
													</Button>
												</Row>
											}
										>
											<KeyValueDisplay
												keyWidth="32px"
												keyAliases={contactInfoKeyAliases}
												keyValueStore={contactInfo}
											/>
										</Card>
									</div>
									<div className="rowSpacing">
										<Card className="materialCard" bodyStyle={{ padding: 0 }}>
											<div className="materialCard__body">
												<TwoLineList
													listItems={detailsDisplay}
													lineOneRender={(record) => record.value || '--'}
													lineTwoRender={(record) => record.key}
												/>
											</div>
										</Card>
									</div>
								</Col>
							</Row>
						</Col>
					</Row>
				</div>
			)}

			<Modal
				title="Invite to Client Portal"
				visible={showPrimaryContactEmailModal}
				onCancel={() => setShowPrimaryContactEmailModal(false)}
				footer={[
					<Button key="cancel" onClick={() => setShowPrimaryContactEmailModal(false)}>
						Cancel
					</Button>,
					<Button
						key="submit"
						type="primary"
						loading={submittingBuyerCompany}
						onClick={() => formRef.current.submit()}
					>
						Create Client Portal
					</Button>,
				]}
				destroyOnClose
			>
				<Form
					ref={formRef}
					layout="vertical"
					initialValues={{ primaryContactEmail: contractorClient.primaryContactEmail || '' }}
					onFinish={handleSubmitBuyerCompany}
				>
					<Form.Item
						name="primaryContactEmail"
						label="Primary Contact Email"
						rules={[
							{ required: true, message: 'Primary contact email is required' },
							{ type: 'email', message: 'Please enter a valid email address' },
						]}
					>
						<Input
							type="email"
							placeholder="Enter primary contact email"
							disabled={submittingBuyerCompany}
						/>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="Client Portal Invite"
				visible={showClientPortalInviteModal}
				onCancel={() => setShowClientPortalInviteModal(false)}
				onOk={() => clientPortalInviteFormRef.current.submit()}
				okText="Send Invite"
				confirmLoading={clientPortalInviteLoading}
				destroyOnClose
			>
				<Form ref={clientPortalInviteFormRef} layout="vertical" onFinish={handleClientPortalInvite}>
					<Form.Item
						label={'Name'}
						className={'flex flex-row'}
						required
						rules={[{ required: true, message: 'Please input name.' }]}
					>
						<div className={'flex flex-row'} style={{ marginBottom: -24 }}>
							<Form.Item
								name={'nameGiven'}
								required
								rules={[{ required: true, message: 'Required.' }]}
							>
								<Input placeholder="Given name" style={{ maxWidth: 200 }} disabled={false} />
							</Form.Item>
							<Form.Item
								name={'nameFamily'}
								required
								rules={[{ required: true, message: 'Required.' }]}
							>
								<Input
									placeholder="Family name"
									style={{ maxWidth: 200, marginLeft: 8 }}
									disabled={false}
								/>
							</Form.Item>
						</div>
					</Form.Item>

					<EmailComponentAntdV4
						fieldProps={{
							preserve: false,
							label: 'Email Address',
							name: 'email',
							rules: [
								{
									required: true,
									message: 'Please enter an email address',
								},
							],
						}}
						placeholder="Enter email address"
					/>
				</Form>
			</Modal>
		</Content>
	);
};
const mapStateToProps = (state, ownProps) => ({
	match: ownProps.match,
	history: ownProps.history,
	contractorClient: getEntityById(ownProps.match.params.id, state.contractor_clients),
	contractorClientLoading: state.contractor_clients.fetching,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateNavbar: (backlinkTitle, backlinkTo, navbarBackgroundColor) =>
		dispatch(updateNavbarState(backlinkTitle, backlinkTo, navbarBackgroundColor)),
	fetchContractorClient: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.readOne(id)
				: contractorClientsRestCrudThunksForBuyer.readOne(id)
		),
	updateContractorClient: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.update(entity)
				: contractorClientsRestCrudThunksForBuyer.update(entity)
		),
	fetchPortals: (
		params,
		targetCollectionName = null,
		pagination = null,
		sorting = null,
		filters = null,
		addToTargetCollection = false
	) =>
		dispatch(
			portalsCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	createClientPortal: (data) => dispatch(portalsCrudThunksForSupplier.create(data)),
	createBuyerCompany: (data) => dispatch(buyersRestCrudThunksForSupplier.create(data)),
	inviteAdminsForClientBuyerCompany: (id, email, contact) =>
		dispatch(inviteAdminsForClientBuyerCompany(id, email, contact)),
	fetchContractorLocations: (params) =>
		dispatch(contractorLocationsRestCrudThunksForSupplier.read(params)),
});

export default withRouter<any, any>(
	withUserType(
		withCurrentUser(connect(mapStateToProps, mapDispatchToProps)(ContractorClientDetailDetailsPage))
	)
);
